import React, { useCallback, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { coverEditorMessages } from '../../coverEditorMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RightsSheetProps {
  isOpen: boolean;
  artistName: string;
  isBusy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The rights of the reference pictures, asked once for each artist (spec §5): three short rules
 * and a tick. "Add the pictures" sends the request with `referenceRightsConfirmed`.
 */
const RightsSheetComponent: React.FC<RightsSheetProps> = ({ isOpen, artistName, isBusy, onConfirm, onCancel }) => {
  const intl = useIntl();
  const tickId = useId();
  const [isTicked, setIsTicked] = useState(false);
  const tick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setIsTicked(event.target.checked), []);
  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(coverEditorMessages.rightsTitle)}
      description={intl.formatMessage(coverEditorMessages.rightsText)}
      confirmLabel={intl.formatMessage(coverEditorMessages.rightsConfirm)}
      isBusy={isBusy}
      isConfirmDisabled={!isTicked}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <ul className={styles.rightsList}>
        <li>{intl.formatMessage(coverEditorMessages.rightsRuleOwn)}</li>
        <li>{intl.formatMessage(coverEditorMessages.rightsRulePeople)}</li>
        <li>{withBrandWord(intl.formatMessage(coverEditorMessages.rightsRuleUse))}</li>
      </ul>
      <label htmlFor={tickId} className={styles.rightsTick}>
        <input id={tickId} type="checkbox" checked={isTicked} onChange={tick} />
        <span>{intl.formatMessage(coverEditorMessages.rightsTick, { artist: artistName })}</span>
      </label>
    </ConfirmDialog>
  );
};

export const RightsSheet = TypedMemo(RightsSheetComponent);
